import { html } from 'lit';

import { customElement, property } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { Mix } from '@kisters/wiski-web/common';
import { WwpOverview } from '@kisters/wiski-web/components';
import LegendFactory from '@kisters/wiski-web/components/ki-station-map/LegendFactory';
import { getCurrentApi } from '@kisters/wiski-web/api/API';
import style from './wwp-hdoe-start.css';

/**
 * This component shows the "Oberflächengewässer -> Pegel" layer.
 * "Fremdpegel" are filtered out on the start page by default (customer request, see WW-423)
 */
@customElement('wwp-hdoe-start')
export class WwpHdoeStart extends Mix(WwpOverview) {
  static styles = [WwpOverview.styles, style];

  startLayers = [
      {
        type: 'topojson',
        url: './maps/abdeckung.json',
        crs: 'EPSG:3857',
        label: 'Länder nah',
        zIndex: 29,
        selectable: false,
        visible: true,
        group: 'Layers',
        style: {
          stroke: {
            color: 'rgba(255, 255, 255, 1)',
          },
          fill: {
            color: 'rgba(255, 255, 255, 1)',
          },
        },
      },
      {
        type: 'GeoJSON',
        url: './maps/ooe.json',
        crs: 'EPSG:3857',
        label: 'ooe abdeckung',
        zIndex: 30,
        selectable: false,
        visible: true,
        group: 'Layers',
        style: {
          stroke: {
            color: 'rgba(29, 29, 29, 3)',
          },
          fill: {
            color: 'rgba(255, 255, 255, 0)',
          },
        },
      },
      {
        type: 'GeoJSON',
        url: './maps/seen.json',
        crs: 'EPSG:3857',
        label: 'Seen',
        visible: true,
        group: 'Layers',
        selectable: false,
        zIndex: 28,
        style: {
          stroke: {
            color: 'rgba(0, 77, 168, 1)',
            width: 1,
          },
          fill: {
            color: 'rgba(0, 112, 255, 1)',
          },
        },
      },
      {
        "label": "Geoland Basemap Grau",
        "type": "WMTS",
        "url": "https://mapsneu.wien.gv.at/basemapneu/1.0.0/WMTSCapabilities.xml",
        "layer": "bmapgrau",
        "matrixSet": "google3857",
        "visible": true
      },
  ];

  api = getCurrentApi();

  // eslint-disable-next-line class-methods-use-this
  getDocHeight(doc) {
    doc = doc || document;
    const __body = doc.body;
    const __html = doc.documentElement;
    const height = Math.max(
      __body.scrollHeight,
      __body.offsetHeight,
      __html.clientHeight,
      __html.scrollHeight,
      __html.offsetHeight,
    );
    return height;
  }

  setIframeHeight(e) {
    const ifrm = e.target;
    if (ifrm) {
      let doc;
      if (ifrm.contentDocument) {
        doc = ifrm.contentDocument;
      } else {
        doc = ifrm.contentWindow ? ifrm.contentWindow.document : null;
      }
      if (doc) {
        ifrm.style.visibility = 'hidden';
        ifrm.style.height = '10px'; // reset to minimal height ...
        // IE opt. for bing/msn needs a bit added or scrollbar appears
        ifrm.style.height = `${this.getDocHeight(doc) + 4}px`;
        ifrm.style.minHeight = `${this.getDocHeight(doc) + 4}px`;

        ifrm.style.visibility = 'visible';
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  get viewStart() {
    const is1540pxOrLess = window.matchMedia('(max-width: 1540px)').matches;
    const is1245pxOrLess = window.matchMedia('(max-width: 1245px)').matches;
    const is1540pxOrMore = window.matchMedia('(min-width: 1540px)').matches;
    
    let zoom = 7;
    if(this.classList.contains('lg-screen')) 
        zoom = 7.8;
    if(this.classList.contains('lg-screen') && (is1540pxOrLess) )
        zoom = 7.2;
    if(this.classList.contains('sm-screen') && (is1245pxOrLess) )
        zoom = 7.3;
    
    return {
      projection: 'EPSG:3857',
      center: [1547773, 6126425],
      maxZoom: zoom,
      minZoom: zoom,
      zoom,
      extent: [983646.5, 5836117.8, 2007460.6, 6299120.7],
    };
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();
    let resize;
    this.lagebericht = fetch(
      `${
        this.api.dataPath
      }/html/Lagebericht.html?cachebust=${new Date().getTime()}`,
    ).then(r => r.text().then(t => unsafeHTML(t)));

    this.aktuelles = fetch(
      `${
        this.api.dataPath
      }/html/Aktuelles.html?cachebust=${new Date().getTime()}`,
    ).then(r => r.text().then(t => unsafeHTML(t)));
    this.iframeResize = window.addEventListener('resize', () => {
      window.clearTimeout(resize);
      resize = setTimeout(() => {
        this.setIframeHeight({
          target: this.renderRoot.getElementById('lagebericht'),
        });
        this.setIframeHeight({
          target: this.renderRoot.getElementById('aktuelles'),
        });
      }, 220);
    });
  }

  _getMaxHeight() {
    if (this.classList.contains('sm-screen')) {
      return `${this.renderRoot.host.clientHeight - 40}px`;
    }
    return 'inital';
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) super.disconnectedCallback();
    window.removeEventListener('resize', this.iframeResize);
  }

  @property({ type: Object })
  private wasserstand = {
    stations: [],
    selectedMarkers: null,
    markers: null
  };

  @property({ type: Object })
  private niederschlag = {
    stations: [],
    selectedMarkers: null,
    markers: null
  };

  @property({ type: Object })
  private grundwasser = {
    stations: [],
    selectedMarkers: null,
    markers: null
  };

  async firstUpdated() {
    this.wasserstand.stations = await this.getStations("Wasserstand");

    this.wasserstand.markers = this.legendFactory.markers
    this.wasserstand.selectedMarkers = this.legendFactory.selectedMarkers
   
    this.niederschlag.stations = await this.getStations("Niederschlag-24h");
    this.niederschlag.markers = this.legendFactory.markers
    this.niederschlag.selectedMarkers = this.legendFactory.selectedMarkers

    this.grundwasser.stations = await this.getStations("Grundwasserstand");
    this.grundwasser.markers = this.legendFactory.markers
    this.grundwasser.selectedMarkers = this.legendFactory.selectedMarkers

    this.requestUpdate(); 
  }

  async getStations(layerName: string) {
    this.layerName = layerName;
    await this.getDataLayer();
    const stations = this.filteredStations.filter((s:any)=> !s.__tag.includes("fremdpegel"));
    return stations;
  }

  // Define the element's template
  render() {  
    return (
      this._renderLoader() ||
      html`
        <div class="leftBorder"></div>
        <div class="main-container">
          <!-- Top section with Lagebericht and warnings -->
          <div class="top-section">
            <div class="lagebericht-section">
              <div class="titleNode">Lagebericht</div>
              <div class="content-wrapper">
                ${until(this.lagebericht, html`<span>Lade...</span>`)}
              </div>
            </div>

            <div class="warnings-section">
              <div class="warning-item">
                <div class="titleNodeWarning">Hydro Hochwasserwarnungen</div>
                <div class="warning-content">
                  <svg
                    title="https://hydro.ooe.gv.at/#/overview/Wasserstand-Alarmpegel"
                    onclick="window.open('https://hydro.ooe.gv.at/#/overview/Wasserstand-Alarmpegel', '_blank')"
                    width="96"
                    height="96"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="cursor: pointer;"
                  >
                    <path
                      d="M12 2L22 20H2L12 2Z"
                      stroke="rgba(81,86,96, 1)"
                      stroke-width="1"
                      fill="rgba(255, 255, 0, 1)"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 9V13"
                      stroke="rgba(81,86,96, 1)"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <circle
                      cx="12"
                      cy="17"
                      r="1"
                      fill="rgba(81,86,96, 1)"
                    />
                  </svg>
                </div>
              </div>

              <div class="warning-item">
                <div class="titleNodeWarning">GeoSphere Austria Warnungen</div>
                <div class="warning-content">
                  <img
                    title="https://warnungen.zamg.at/html/de/heute/alle/at/"
                    alt="ZAMG Warnungen"
                    onclick="window.open('https://warnungen.zamg.at/html/de/heute/alle/at/', '_blank')"
                    src="//warnungen.zamg.at/warnwidget/de/heute/alle/at/"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Maps section - 3 maps side by side -->
          <div class="maps-section">
            <div class="map-item">
              <div class="titleNode">Wasserstand</div>
              <div class="overlay" onclick='window.location.hash= "/overview/Wasserstand"'></div>
              <ki-station-map id="map" .view="${this.viewStart}" .layers="${this.startLayers}">
                <ki-stations-layer
                  .tagMarkers="${this.wasserstand.markers}"
                  .tagSelectedMarkers="${this.wasserstand.selectedMarkers}"
                  .stations="${this.wasserstand.stations}"
                ></ki-stations-layer>
              </ki-station-map>
            </div>

            <div class="map-item">
              <div class="titleNode">Niederschlag</div>
              <div class="overlay" onclick='window.location.hash= "/overview/Niederschlag-24h"'></div>
              <ki-station-map id="map" .view="${this.viewStart}" .layers="${this.startLayers}">
                <ki-stations-layer
                  .tagMarkers="${this.niederschlag.markers}"
                  .tagSelectedMarkers="${this.niederschlag.selectedMarkers}"
                  .stations="${this.niederschlag.stations}"
                ></ki-stations-layer>
              </ki-station-map>
            </div>

            <div class="map-item">
              <div class="titleNode">Grundwasser</div>
              <div class="overlay" onclick='window.location.hash= "/overview/Grundwasserstand"'></div>
              <ki-station-map id="map" .view="${this.viewStart}" .layers="${this.startLayers}">
                <ki-stations-layer
                  .tagMarkers="${this.grundwasser.markers}"
                  .tagSelectedMarkers="${this.grundwasser.selectedMarkers}"
                  .stations="${this.grundwasser.stations}"
                ></ki-stations-layer>
              </ki-station-map>
            </div>
          </div>

          <!-- Aktuelles section at bottom -->
          <div class="aktuelles-section">
            <div class="titleNode">Aktuelles</div>
            <div class="content-wrapper">
              <div class="aktuelles-html">
                ${until(this.aktuelles, html`<span>Lade…</span>`)}
              </div>
            </div>
          </div>
        </div>
        <div class="rightBorder"></div>
      `
    );
  }

  get legendFactory() {
    const copy = { ...this._dataLayer.config.classification };
    if (copy && copy.default && copy.default.marker) {
      copy.default.marker.radius = 10;
    }
    return new LegendFactory(copy);
  }
}